import { DatePickerSelectedValue } from '@storeblocks/datepicker';
import { format } from 'date-fns';
import styled from 'styled-components';

import { Show } from '@/components/Show';
import { DateSearch } from '@/components/Toolbar/DateSearch';
import { DownloadButton } from '@/components/Toolbar/DownloadButton';
import { DownloadErrorAlert } from '@/components/Toolbar/DownloadErrorAlert';
import { useSearchParams } from '@/components/Toolbar/hooks/useSearchParams';
import { TextSearch } from '@/components/Toolbar/TextSearch';
import { useHandleAsyncOperation } from '@/hooks/useHandleAsyncError';
import { useTexts } from '@/hooks/useTexts';
import { media } from '@/styles/variables';
import { isBetaFeaturesOn } from '@/util/isBetaFeaturesOn';
import { stripFilename } from '@/util/stripFilename';

import { useReportsContext } from '../context/ReportsProvider';
import { ReportSource } from '../types/ReportModel';
import { downloadReports } from '../utils/downloadReports';
import { ReportSourceFilter } from './ReportSourceFilter';

export interface SearchParams {
  date?: string;
  query?: string;
  sources: ReportSource[];
}

interface Props {
  onSearchParamsChange: (params: SearchParams) => void;
}

export function ReportsToolbar({
  onSearchParamsChange,
}: Props): React.ReactElement {
  const texts = useTexts();
  const { selectedReports } = useReportsContext();

  const searchParams = useSearchParams<SearchParams>(onSearchParamsChange, {
    sources: [],
  });

  const [handleDownloadOnClick, , downloadError] = useHandleAsyncOperation(
    async () => {
      await downloadReports([...selectedReports.values()]);
    },
  );

  const handleTextSearchChanged = (query: string): void => {
    searchParams.update((state) => {
      state.query = query;
    });
  };

  const handleDateSearchChanged = ({ date }: DatePickerSelectedValue): void => {
    const dateString = date ? format(date, 'yyyy-MM-dd') : '';

    searchParams.update((state) => {
      state.date = dateString;
    });
  };

  const handleReportsSourceChanged = (sources: ReportSource[]): void => {
    searchParams.update((state) => {
      state.sources = sources;
    });
  };

  return (
    <>
      <Wrapper>
        <TextSearch
          onChange={handleTextSearchChanged}
          label={texts.reportsArchivePage.search}
        />

        <DateSearch
          onChange={handleDateSearchChanged}
          label={texts.reportsArchivePage.tableHeaders.reportDate}
        />

        <DownloadButton
          onClick={handleDownloadOnClick}
          numberOfFilesToDownload={selectedReports.size}
          data-trackid-downloaded-files={[...selectedReports.values()].map(
            (report) => stripFilename(report.name),
          )}
        />

        {isBetaFeaturesOn() && (
          <ReportSourceFilter onChange={handleReportsSourceChanged} />
        )}
      </Wrapper>

      <Show when={downloadError}>
        <DownloadErrorAlert />
      </Show>
    </>
  );
}

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  // Rows containing input fields must have a minimum height of 110px to allow room for an error alert
  // to be displayed below the input fields.
  grid-template-rows: minmax(110px, auto) auto;
  gap: 16px;

  & > button {
    // Align the download button with the input fields

    margin-top: 30px;
  }

  & > * :nth-child(4) {
    grid-row: 2;
    justify-self: start;
  }

  @media ${media.tabletAndBelow} {
    grid-template-columns: 1fr;
    // Rows containing input fields must have a minimum height of 110px to allow room for an error alert
    // to be displayed below the input fields.
    grid-template-rows: repeat(2, minmax(110px, auto)) repeat(2, auto);

    & > button {
      justify-self: start;
      margin-top: 0;
    }
  }
`;
